// Header ✨ New button. Opens a modal with a "What's New in JobSite Pulse"
// changelog — matches vanilla's app-news dialog.

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

interface WhatsNewEntry {
  icon: string
  iconBg?: string
  title: string
  location?: string
  body: string
  tryPath?: string
}

interface WhatsNewSection {
  date: string
  entries: WhatsNewEntry[]
}

// Hand-curated changelog. Newest first. Add entries to the top of this list.
const CHANGELOG: WhatsNewSection[] = [
  {
    date: 'April 20, 2026',
    entries: [
      {
        icon: '📎', iconBg: 'var(--accent-light)',
        title: 'Receipt scan/attach, check photo, bid file attach',
        location: 'Expenses → Add form / Payments → Record form / Bid Room → Add form',
        body: 'Attach receipts to expenses via camera or file picker. Snap a check photo when recording a payment. Attach PDF contracts, photos, or docs to bids before submitting.',
        tryPath: '/expenses',
      },
      {
        icon: '✏️', iconBg: 'var(--amber-bg)',
        title: 'Bid Room edit modal',
        location: 'Bid Room → Edit button on any bid row',
        body: "Edit any bid's amount, date, sub, linked project/estimate, category, notes, and status in a full modal. Delete bids from the same dialog.",
        tryPath: '/bidroom',
      },
      {
        icon: '⇑', iconBg: 'var(--green-bg)',
        title: 'Expenses CSV import',
        location: 'Expenses → Import CSV button',
        body: 'Re-import a previously exported expense CSV. Columns are auto-detected by header name (Date, Amount, Paid to, Project, Category, Type, Method, Paid, Notes). Each row is added as a new expense.',
        tryPath: '/expenses',
      },
      {
        icon: '💳', iconBg: 'var(--purple-bg)',
        title: 'Account & Subscription — live plan badge',
        location: 'Options → Account & Subscription',
        body: "Your plan tier (Starter/Pro/Team/Business) and seat usage now reads from Firestore. Seat progress bar turns amber when you're near the limit.",
        tryPath: '/options',
      },
    ],
  },
  {
    date: 'April 19, 2026',
    entries: [
      {
        icon: '🃏', iconBg: 'var(--accent-light)',
        title: 'Drag-and-drop on Projects Board & Portfolio Pipeline',
        location: 'Projects → Board view / Portfolio → Pipeline view',
        body: 'Drag a card to another column to instantly move it between statuses or pipeline stages. Columns highlight as you hover.',
        tryPath: '/projects',
      },
      {
        icon: '📊', iconBg: 'var(--green-bg)',
        title: 'Reports: 8 fully-live sub-tabs',
        location: 'Reports tab',
        body: 'Monthly (month-by-month table + chart), By Project, Clients, Cash Flow (running balance), Performance (budget bars), Vendors (spending by payee), and Tax (deductibles + 1099-NEC eligible subs) are all now live.',
        tryPath: '/reports',
      },
      {
        icon: '📤', iconBg: 'var(--amber-bg)',
        title: 'QuickBooks IIF, Xero CSV & OFX exports',
        location: 'Expenses page → export toolbar / Subs → 1099 IIF',
        body: 'Expenses now export as QuickBooks IIF (CHECK transactions), QB CSV, Xero bank statement CSV, and OFX 1.02 SGML. Subs 1099 IIF exports vendor records with NEC flag + EIN for direct QB import.',
        tryPath: '/expenses',
      },
      {
        icon: '📋', iconBg: 'var(--accent-light)',
        title: 'Estimate templates — 6 built-in starters',
        location: 'Estimates tab → "📋 Template" button',
        body: 'Pick from Kitchen Remodel, Bathroom, Deck Build, Roof Replacement, Interior Painting, or Landscaping. Each template pre-fills categories + line items with typical costs you can adjust.',
        tryPath: '/estimates',
      },
    ],
  },
  {
    date: 'April 18, 2026',
    entries: [
      {
        icon: '🧩', iconBg: 'var(--accent-light)',
        title: 'Full v2 rebuild — React + TypeScript',
        location: 'app-v2.jobsitepulse.app',
        body: 'The entire UI has been rebuilt on React + Vite. Same Firestore data, cleaner code, faster loads. Every tab is now componentized with typed domain models.',
      },
      {
        icon: '📊', iconBg: 'var(--green-bg)',
        title: 'Profit & Loss Report',
        location: 'Reports tab',
        body: 'Revenue + expenses + net profit with % margin. Expense breakdown by trade. Revenue breakdown by payment type. CSV exports for expenses, payments, and per-project P&L.',
        tryPath: '/reports',
      },
      {
        icon: '💳', iconBg: 'var(--amber-bg)',
        title: 'Payments — Awaiting deposits + Outstanding by project',
        location: 'Payments tab',
        body: '4-tile metrics row (Total received / Pending / Overdue / Count). Projects with no deposit yet surface in an "Awaiting deposits" block with one-click record. Progress bars per project showing received vs. budget.',
        tryPath: '/payments',
      },
      {
        icon: '💸', iconBg: 'var(--red-bg)',
        title: 'Expenses — mode tabs + full export bar',
        location: 'Expenses tab',
        body: 'Add expense in Project, Business Expense, or Crew Labor mode — each renders a tailored form. Crew Labor auto-calculates hours × rate. Import CSV plus exports to CSV, QuickBooks IIF, QB CSV, Xero CSV, and OFX.',
        tryPath: '/expenses',
      },
      {
        icon: '📐', iconBg: 'var(--accent-light)',
        title: 'Estimates — portal send + Print / PDF',
        location: 'Estimates tab',
        body: 'Create a client portal link from any estimate — copy URL, open in new tab, or mailto to the client. Print/PDF exports the estimate card. Templates are coming.',
        tryPath: '/estimates',
      },
      {
        icon: '👷', iconBg: 'var(--accent-light)',
        title: 'Bid Room — flat bids from subs',
        location: 'Bid Room tab',
        body: 'Track incoming bids: date, amount, sub, project/estimate link, category. Send/Re-send action, filter by any column, push-to-estimate still available via the detail page on a per-bid basis.',
        tryPath: '/bidroom',
      },
      {
        icon: '🧾', iconBg: 'var(--green-bg)',
        title: '1099 toolchain',
        location: 'Subs tab',
        body: 'Year dropdown + one-click 1099 CSV, 1099 IIF, and Tax prep CSV exports. Sort by paid-this-year to surface your biggest subs at the top of the list.',
        tryPath: '/subs',
      },
      {
        icon: '⚙️', iconBg: 'var(--purple-bg)',
        title: 'Options page — 15 expandable sections',
        location: 'Options tab',
        body: 'New layout: Team, Account & Subscription, Business Profile, Business Documents, Client-facing, Welcome Email, Profit Margin, Project Manager Controls, Default Settings, Expenses, Alerts & Notifications, 1099 Settings, Display Preferences, App Theme, Help & Onboarding, Recent Activity, Data Management.',
        tryPath: '/options',
      },
    ],
  },
]

export default function WhatsNewMenu() {
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  function go(path: string) {
    setOpen(false)
    navigate(path)
  }

  return (
    <>
      <button
        className="quick-create-btn"
        onClick={() => setOpen(true)}
        title="What's new in JobSite Pulse"
        aria-label="What's new"
      >
        ✨ New
      </button>

      {open && (
        <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setOpen(false) }}
          style={{ alignItems: 'flex-start', paddingTop: '5vh' }}>
          <div className="modal" style={{ maxWidth: 640, maxHeight: '85vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 700, letterSpacing: '-0.01em' }}>
                  ✨ What's New in JobSite Pulse
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                  Latest features + improvements
                </div>
              </div>
              <button onClick={() => setOpen(false)}
                aria-label="Close"
                style={{ border: 'none', background: 'transparent', color: 'var(--text-faint)', fontSize: 18, cursor: 'pointer', padding: '2px 8px' }}>
                ✕
              </button>
            </div>

            {CHANGELOG.map((section) => (
              <div key={section.date}>
                <div style={{
                  fontSize: 11, fontWeight: 700, letterSpacing: '0.06em',
                  color: 'var(--accent-text)', textTransform: 'uppercase',
                  borderBottom: '2px solid var(--accent)',
                  paddingBottom: 4, marginBottom: 12, marginTop: 8,
                }}>
                  {section.date}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {section.entries.map((e, i) => (
                    <div key={i} className="card" style={{ padding: 12 }}>
                      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                        <div style={{
                          width: 36, height: 36, flexShrink: 0, borderRadius: 10,
                          background: e.iconBg ?? 'var(--surface-alt)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 18,
                        }}>
                          {e.icon}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 14, fontWeight: 700 }}>{e.title}</div>
                          {e.location && (
                            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                              📍 {e.location}
                            </div>
                          )}
                          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 6, lineHeight: 1.45 }}>
                            {e.body}
                          </div>
                          {e.tryPath && (
                            <button className="btn-ghost btn-sm"
                              style={{
                                marginTop: 8, border: '1px solid var(--accent)',
                                color: 'var(--accent-text)', borderRadius: 999,
                                padding: '3px 12px', fontSize: 11, fontWeight: 600,
                              }}
                              onClick={() => go(e.tryPath!)}>
                              Try it →
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  )
}
